import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * ✈️ FLIGHT TIMELINE - The Main Event
 * 
 * Mobile-first, grouped by day, includes:
 * - Flight cards with departure/arrival
 * - Connection times between flights
 * - Deadhead indicators
 * - Red-eye badges
 * - International badges
 * - Layover cards with hotel info
 * - Visual timeline connecting everything
 * 
 * Collapsed by default on mobile, expandable days
 */

const FlightTimeline = ({ flights, layovers, summary }) => {
  const [expandedDays, setExpandedDays] = useState({});

  // 🐛 DEBUG: Log what we received
  console.log('🛫 FlightTimeline received:', { 
    flightsCount: flights?.length, 
    layoversCount: layovers?.length,
    layoversData: layovers 
  });

  // Group flights by day
  const groupedByDay = flights.reduce((groups, flight) => {
    const day = flight.day || 1;
    if (!groups[day]) {
      groups[day] = [];
    }
    groups[day].push(flight);
    return groups;
  }, {});

  const days = Object.keys(groupedByDay).sort((a, b) => a - b);

  const toggleDay = (day) => {
    setExpandedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    if (timeString.includes(':')) return timeString.substring(0, 5);
    return timeString;
  };

  const formatLayoverDuration = (hours) => {
    if (!hours) return 'N/A';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Get layover AFTER this day (between day N and day N+1)
  const getLayoverAfterDay = (dayIndex) => {
    // Layovers array is ordered by duty period
    // layovers[0] is between day 1 and day 2, etc.
    return layovers[dayIndex]; // dayIndex is 0-based, layovers[0] is first overnight
  };

  return (
    <div className="card p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
          <Icon name="Route" className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Flight Timeline</h3>
          <p className="text-sm text-muted-foreground">
            {flights.length} flights • {layovers.length} layovers • {days.length} days
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {days.map((day, dayIndex) => {
          const dayFlights = groupedByDay[day];
          const layoverAfterDay = getLayoverAfterDay(dayIndex);
          const isExpanded = expandedDays[day] !== false; // Expanded by default

          return (
            <div key={day} className="relative">
              {/* Day Header - Clickable to expand/collapse */}
              <button
                onClick={() => toggleDay(day)}
                className="w-full flex items-center gap-3 mb-3 hover:opacity-80 transition-opacity text-left"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent-cyan text-white font-bold text-lg shadow-md flex-shrink-0">
                  {day}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-muted-foreground">DAY {day}</div>
                  <div className="text-lg font-semibold text-foreground">
                    {dayFlights[0]?.calendar_date 
                      ? new Date(dayFlights[0].calendar_date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })
                      : 'N/A'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {dayFlights.length} flights
                  </div>
                </div>
                <Icon 
                  name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                  className="w-5 h-5 text-muted-foreground" 
                />
              </button>

              {/* Flights (expandable) */}
              {isExpanded && (
                <div className="space-y-3 ml-0 md:ml-6 relative">
                  {/* Vertical timeline line (desktop only) */}
                  <div className="hidden md:block absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

                  {dayFlights.map((flight, flightIndex) => (
                    <div key={flightIndex} className="relative">
                      {/* Flight Card */}
                      <div className={`card p-4 ${flight.is_deadhead ? 'border-2 border-warning border-dashed' : ''} hover:shadow-md transition-shadow`}>
                        {/* Flight Number + Badges */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="text-lg font-bold text-foreground">
                              {flight.flight_number}
                            </div>
                            {flight.is_deadhead && (
                              <span className="badge badge-warning">
                                <Icon name="Users" className="w-3 h-3 mr-1" />
                                DH
                              </span>
                            )}
                            {flight.is_red_eye && (
                              <span className="badge badge-error">
                                <Icon name="Moon" className="w-3 h-3 mr-1" />
                                Red-Eye
                              </span>
                            )}
                            {flight.is_international && (
                              <span className="badge badge-accent-cyan">
                                <Icon name="Globe" className="w-3 h-3 mr-1" />
                                Intl
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {flight.aircraft}
                          </div>
                        </div>

                        {/* Route: From → To */}
                        <div className="flex items-center justify-between mb-3">
                          {/* Departure */}
                          <div className="flex-1">
                            <div className="text-2xl font-bold text-foreground">
                              {flight.from}
                            </div>
                            {/* Three Timezones */}
                            {flight.departure_times ? (
                              <div className="text-xs space-y-0.5">
                                <div className="text-muted-foreground">
                                  <span className="font-medium text-foreground">{formatTime(flight.departure_times.local)}</span> <span className="text-xs opacity-70">Local</span>
                                </div>
                                <div className="text-muted-foreground">
                                  <span className="font-medium">{formatTime(flight.departure_times.base)}</span> <span className="text-xs opacity-70">Base</span>
                                </div>
                                <div className="text-muted-foreground">
                                  <span className="font-medium">{formatTime(flight.departure_times.zulu)}</span> <span className="text-xs opacity-70">Zulu</span>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                {formatTime(flight.scheduled_departure)}
                              </div>
                            )}
                            {flight.gate_from && (
                              <div className="text-xs text-muted-foreground">
                                Gate {flight.gate_from}
                              </div>
                            )}
                          </div>

                          {/* Arrow + Block Time */}
                          <div className="flex-shrink-0 px-4 text-center">
                            <Icon name="ArrowRight" className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                            <div className="text-xs font-medium text-accent-cyan">
                              {flight.block_time}
                            </div>
                          </div>

                          {/* Arrival */}
                          <div className="flex-1 text-right">
                            <div className="text-2xl font-bold text-foreground">
                              {flight.to}
                            </div>
                            {/* Three Timezones */}
                            {flight.arrival_times ? (
                              <div className="text-xs space-y-0.5">
                                <div className="text-muted-foreground">
                                  <span className="font-medium text-foreground">{formatTime(flight.arrival_times.local)}</span> <span className="text-xs opacity-70">Local</span>
                                </div>
                                <div className="text-muted-foreground">
                                  <span className="font-medium">{formatTime(flight.arrival_times.base)}</span> <span className="text-xs opacity-70">Base</span>
                                </div>
                                <div className="text-muted-foreground">
                                  <span className="font-medium">{formatTime(flight.arrival_times.zulu)}</span> <span className="text-xs opacity-70">Zulu</span>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                {formatTime(flight.scheduled_arrival)}
                              </div>
                            )}
                            {flight.gate_to && (
                              <div className="text-xs text-muted-foreground">
                                Gate {flight.gate_to}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Connection Time (if next flight same day) */}
                        {flightIndex < dayFlights.length - 1 && (
                          <div className="pt-3 border-t border-border flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="Clock" className="w-4 h-4" />
                            <span>Connection: {/* TODO: Calculate sit time */}N/A</span>
                          </div>
                        )}
                      </div>

                      {/* Connection indicator (desktop only) */}
                      {flightIndex < dayFlights.length - 1 && (
                        <div className="hidden md:flex items-center justify-center h-6">
                          <Icon name="MoveDown" className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Layover Card (overnight between this day and next) */}
                  {layoverAfterDay && (
                    <div className="relative mt-6">
                      <div className={`card p-4 border-2 ${layoverAfterDay.is_min_rest ? 'border-warning' : 'border-primary'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full ${layoverAfterDay.is_min_rest ? 'bg-warning/20' : 'bg-primary/20'} flex items-center justify-center flex-shrink-0`}>
                            <Icon name="Hotel" className={`w-5 h-5 ${layoverAfterDay.is_min_rest ? 'text-warning' : 'text-primary'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-semibold text-foreground">
                                  Layover in {layoverAfterDay.station}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {formatLayoverDuration(layoverAfterDay.duration_hours)} rest
                                  {layoverAfterDay.is_min_rest && (
                                    <span className="ml-2 text-warning font-medium">⚠️ Minimum Rest</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Hotel Info */}
                            {layoverAfterDay.hotel && (
                              <div className="mt-3 p-3 rounded-lg bg-muted/50 space-y-1">
                                <div className="flex items-center gap-2 text-sm">
                                  <Icon name="Building" className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-medium text-foreground">{layoverAfterDay.hotel.name}</span>
                                </div>
                                {layoverAfterDay.hotel.phone && layoverAfterDay.hotel.phone !== 'N/A' && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Icon name="Phone" className="w-4 h-4" />
                                    <a href={`tel:${layoverAfterDay.hotel.phone}`} className="hover:text-primary">
                                      {layoverAfterDay.hotel.phone}
                                    </a>
                                  </div>
                                )}
                                {layoverAfterDay.hotel.address && layoverAfterDay.hotel.address !== 'N/A' && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Icon name="MapPin" className="w-4 h-4" />
                                    <span>{layoverAfterDay.hotel.address}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Day separator (not for last day) */}
              {dayIndex < days.length - 1 && (
                <div className="my-6 border-t-2 border-dashed border-border" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlightTimeline;
